/**
 * The airship's building kit (ADR 0033), lifted from brad-airship: the base
 * material set of `airship-world.js` `buildAirship`, the geometry helpers of
 * `airship-exterior.js` (`tag`, `primitive`, `box`, `beam`) and
 * `airship-prefabs.js` (`tagGeometry`, `addBlock`), and the flags the
 * prototype kept on `material.userData` and `mesh.userData`, here in typed
 * weak maps (`flags`, `tagMesh`). One kit per built ship scene: its
 * materials, textures and uniforms live until the scene is replaced, and every
 * builder of that scene shares them, so the batcher (`world.ts`) merges their
 * geometry by material.
 *
 * Builders work in three.js's axes in the prototype's coordinates (x toward
 * the bow, y up, z south: toward the camera), as the prototype authored them;
 * the game's axes are (x, −z) on the ground, so a scene record's (x, y) is
 * three's (x, −y).
 *
 * The cutaway around the traveler (`cutaway.ts` `townCutawayHook`, the shared
 * engine's `installArchitectureCutaway` with its defaults) is on every
 * material, and geometry tagged `lwCutaway` 1 opens in it (`tag(g, true)`);
 * below deck every material also samples the room's radiance volume
 * (`finish`).
 */
import * as THREE from 'three';
import { townCutawayHook } from '../cutaway.ts';
import { addShaderHook } from '../material-hooks.ts';
import { type CloudUniforms, type LampUniforms, type RadianceUniforms, type WindUniforms, createCloudUniforms, createLampUniforms, createRadianceUniforms, createWindUniforms, radianceHook } from './hooks.ts';
import { type SurfaceTextures, createSurfaceTextures } from './surfaces.ts';

export type Vec3 = readonly [number, number, number];

/** What the prototype kept on `material.userData`: how the world batches, shadows and lights a material. */
export type MaterialFlags = {
  /** Emissive intensity at night: the time of day sets `emissiveIntensity = nightGlow × lamps` (lantern glass, lit windows, navigation lights). */
  nightGlow?: number;
  /** A lift stone's glow: it breathes with the flight and the night (`flight.ts`). */
  liftGlow?: number;
  /** Casts no shadow (glass, lamps, lift stones, flat dark fills): no shadow-pass draw. */
  noShadow?: boolean;
  /** Small detail (balusters, fittings, lantern cages): casts only at the High preset in the prototype; Medium keeps it. */
  detailShadow?: boolean;
  /** A tint-only variant: batched into this vertex-coloured base with its colour baked into the geometry. */
  batchAs?: THREE.MeshStandardMaterial;
  /** Attributes every geometry batched with this material must have (zero-filled where missing). */
  fillAttributes?: readonly (readonly [string, number])[];
};
const materialFlags = new WeakMap<THREE.Material, MaterialFlags>();
/** A material's flags (created empty on first use). */
export const flags = (material: THREE.Material): MaterialFlags => {
  let f = materialFlags.get(material);
  if (f === undefined) {
    f = {};
    materialFlags.set(material, f);
  }
  return f;
};

/** What the prototype kept on `mesh.userData`: a named part that fades as one (the mooring, the set sails), the room whose roof it is, a mesh the world must not batch. */
export type MeshTags = { part?: string; reveal?: string; dynamic?: boolean };
const meshTags = new WeakMap<THREE.Object3D, MeshTags>();
export const tagsOf = (o: THREE.Object3D): MeshTags => {
  let t = meshTags.get(o);
  if (t === undefined) {
    t = {};
    meshTags.set(o, t);
  }
  return t;
};
export const tagMesh = <T extends THREE.Object3D>(o: T, tags: MeshTags): T => {
  Object.assign(tagsOf(o), tags);
  return o;
};

export type ShipMaterials = Record<string, THREE.MeshStandardMaterial>;

export type ShipKit = {
  /** The materials by key: the base set and whatever the builders add (`exteriorMaterials`, the rooms'). */
  readonly m: ShipMaterials;
  /** The material of `key` (a builder's own key must exist: it throws otherwise). */
  get(key: string): THREE.MeshStandardMaterial;
  /** The procedural surface textures (`surfaces.ts`), made once per kit on first use. */
  textures(): SurfaceTextures;
  /** The room's radiance volume's uniforms below deck (null on the deck, whose volume is installed after the build). */
  readonly radiance: RadianceUniforms | null;
  readonly wind: WindUniforms;
  readonly cloud: CloudUniforms;
  readonly lamp: LampUniforms;
  /** Gives a new material the kit's finish: the cutaway, and below deck the radiance volume (the prototype's `installArchitectureCutaway` and `installCaveRadiance`). */
  finish<T extends THREE.Material>(material: T): T;
  /** Registers something to dispose with the kit (a texture, a material made outside `m`). */
  own<T extends { dispose(): void }>(resource: T): T;
  /** A random stream from a seed (an LCG, the prototype's `rng`). */
  random(seed: number): () => number;
  dispose(): void;
};

/** The base colours of `buildAirship` (`airship-world.js`). */
const baseColors: Readonly<Record<string, string>> = {
  floor: '#947451',
  timber: '#6e543e',
  cream: '#c8bea1',
  trim: '#615a4c',
  cliff: '#737d7d',
  blue: '#4c758d',
  green: '#718e68',
  red: '#a34e44',
  soil: '#685c48',
  warm: '#d9b36e',
  marker: '#c6b487',
};

export const createShipKit = (options: { readonly interior: boolean }): ShipKit => {
  const m: ShipMaterials = {};
  const owned: { dispose(): void }[] = [];
  const radiance = options.interior ? createRadianceUniforms() : null;
  let textures: SurfaceTextures | null = null;
  const finish = <T extends THREE.Material>(material: T): T => {
    addShaderHook(material, townCutawayHook);
    if (radiance !== null) addShaderHook(material, radianceHook(radiance));
    return material;
  };
  for (const [key, color] of Object.entries(baseColors)) {
    const mat = new THREE.MeshStandardMaterial({ color, roughness: 1 });
    mat.name = `airship-base-${key}`;
    m[key] = finish(mat);
  }
  return {
    m,
    get(key) {
      const mat = m[key];
      if (mat === undefined) throw new Error(`ship kit: no material ${key}`);
      return mat;
    },
    textures() {
      textures ??= createSurfaceTextures();
      return textures;
    },
    radiance,
    wind: createWindUniforms(),
    cloud: createCloudUniforms(),
    lamp: createLampUniforms(),
    finish,
    own(resource) {
      owned.push(resource);
      return resource;
    },
    random(seed) {
      let s = seed >>> 0;
      return () => {
        s = (Math.imul(s, 1664525) + 1013904223) >>> 0;
        return s / 4294967296;
      };
    },
    dispose() {
      for (const mat of Object.values(m)) mat.dispose();
      textures?.dispose();
      for (const r of owned) r.dispose();
    },
  };
};

/** Marks a geometry for the cutaway: `lwCutaway` 1 opens around the traveler, 0 never does (`airship-exterior.js` `tag`). */
export const tag = <G extends THREE.BufferGeometry>(g: G, cut = false): G => {
  g.setAttribute('lwCutaway', new THREE.Float32BufferAttribute(new Float32Array(g.getAttribute('position').count).fill(cut ? 1 : 0), 1));
  return g;
};

/** A mesh of `g` at (x, y, z) in `root` (`airship-exterior.js` `primitive`). */
export const primitive = (root: THREE.Object3D, mat: THREE.Material, g: THREE.BufferGeometry, x = 0, y = 0, z = 0, cut = false): THREE.Mesh => {
  const o = new THREE.Mesh(tag(g, cut), mat);
  o.position.set(x, y, z);
  root.add(o);
  return o;
};

/** A box w × h × d centred at (x, y, z) (`box`; the prototype's `addBlock` has the same arguments). */
export const box = (root: THREE.Object3D, mat: THREE.Material, x: number, y: number, z: number, w: number, h: number, d: number, cut = false): THREE.Mesh =>
  primitive(root, mat, new THREE.BoxGeometry(w, h, d), x, y, z, cut);

/** A rod from a to b of radius r (`beam`): thin rods (under 6 cm) skip their end caps. */
export const beam = (root: THREE.Object3D, mat: THREE.Material, a: Vec3, b: Vec3, r = 0.06, sides = 6, cut = false): THREE.Mesh => {
  const av = new THREE.Vector3(...a);
  const bv = new THREE.Vector3(...b);
  const v = bv.clone().sub(av);
  const mid = av.add(bv).multiplyScalar(0.5);
  const o = primitive(root, mat, new THREE.CylinderGeometry(r, r, v.length(), sides, 1, r < 0.06), mid.x, mid.y, mid.z, cut);
  o.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), v.normalize());
  return o;
};

/** Narrows to a mesh with three's default geometry and material types (an `instanceof` alone narrows to `Mesh<any>`). */
export const isMesh = (o: THREE.Object3D): o is THREE.Mesh => o instanceof THREE.Mesh;

/** Element `i` of a list a builder knows is there (an authored table or a loop's own index): throws on a slip rather than drawing at NaN. */
export const at = <T>(list: readonly T[], i: number): T => {
  const v = list[i];
  if (v === undefined) throw new Error(`ship: index ${i} past ${list.length}`);
  return v;
};

/** The last element of a non-empty list. */
export const last = <T>(list: readonly T[]): T => at(list, list.length - 1);
